/**
 * Toast notification composable
 * Provides a simple, reactive toast notification system
 */
import { ref, readonly } from 'vue'

// Shared state for toasts
const toasts = ref([])
let toastId = 0

/**
 * Add a toast notification
 * @param {Object} options - Toast options
 * @param {string} options.message - Toast message
 * @param {string} options.type - Toast type: 'success' | 'error' | 'warning' | 'info'
 * @param {number} options.duration - Duration in ms (default: 3000)
 */
function addToast({ message, type = 'info', duration = 3000 }) {
  const id = ++toastId

  toasts.value.push({
    id,
    message,
    type,
    duration,
  })

  // Auto-remove after duration
  if (duration > 0) {
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }

  return id
}

/**
 * Remove a toast by ID
 * @param {number} id - Toast ID to remove
 */
function removeToast(id) {
  const index = toasts.value.findIndex((t) => t.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

/**
 * Clear all toasts
 */
function clearToasts() {
  toasts.value = []
}

// Convenience methods
function success(message, duration = 3000) {
  return addToast({ message, type: 'success', duration })
}

function error(message, duration = 4000) {
  return addToast({ message, type: 'error', duration })
}

function warning(message, duration = 3500) {
  return addToast({ message, type: 'warning', duration })
}

function info(message, duration = 3000) {
  return addToast({ message, type: 'info', duration })
}

/**
 * Toast composable hook
 * @returns {Object} Toast methods and state
 */
export function useToast() {
  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
  }
}

// Export for direct usage
export { toasts, addToast, removeToast, clearToasts, success, error, warning, info }
